import { useCallback, useEffect, useMemo, useState } from 'react';
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from '../firebase';
import {
  effectivePlanForUser,
  ROLLING_CAPTURE_PERIOD_MS,
} from '../constants/uploadLimits';
import {
  countCompletedCapturesAcrossStudio,
  countCompletedCapturesInRollingWindow,
} from './useProjects';
import { useStudioMetrics } from './studioMetrics';
import { mergeFreeLifetimeCaptureTotals } from './studioMetrics/studioMetricsComputations';

export { mergeFreeLifetimeCaptureTotals };

export function useStudioTopBarPlanPill(user) {
  const studioUidForUsage = user?.workspaceRoutingUid || user?.uid || null;
  const planKey = useMemo(() => effectivePlanForUser(user), [user]);
  const studioMetrics = useStudioMetrics();
  const metricsActive = !!(studioMetrics?.active && studioMetrics.studioUid === studioUidForUsage);
  const metricsFromContext = metricsActive ? studioMetrics.metricsCaptureTotal : null;
  const quotaFromContext = metricsActive ? studioMetrics.quotaCounts : null;

  const [metricsCaptureTotal, setMetricsCaptureTotal] = useState(null);
  const [studioVerifiedCaptureTotal, setStudioVerifiedCaptureTotal] = useState(null);
  const [proPeriodCaptureCount, setProPeriodCaptureCount] = useState(null);

  useEffect(() => {
    if (metricsFromContext != null) {
      setMetricsCaptureTotal(metricsFromContext);
    }
  }, [metricsFromContext]);

  useEffect(() => {
    if (quotaFromContext?.completedCount != null) {
      setStudioVerifiedCaptureTotal(quotaFromContext.completedCount);
    }
  }, [quotaFromContext?.completedCount]);

  useEffect(() => {
    if (quotaFromContext?.rollingCompletedCount != null) {
      setProPeriodCaptureCount(quotaFromContext.rollingCompletedCount);
    }
  }, [quotaFromContext?.rollingCompletedCount]);

  const refreshStudioCaptureTotal = useCallback(async () => {
    if (!user?.uid || !studioUidForUsage) return;
    if (quotaFromContext?.completedCount != null) {
      setStudioVerifiedCaptureTotal(quotaFromContext.completedCount);
      return;
    }
    try {
      const n = await countCompletedCapturesAcrossStudio(studioUidForUsage);
      setStudioVerifiedCaptureTotal(n);
    } catch {
      setStudioVerifiedCaptureTotal(0);
    }
  }, [quotaFromContext?.completedCount, studioUidForUsage, user?.uid]);

  const refreshProPeriodCaptureCount = useCallback(async () => {
    if (!user?.uid || !studioUidForUsage) return;
    if (planKey !== 'professional' && planKey !== 'studio') return;
    if (quotaFromContext?.rollingCompletedCount != null) {
      setProPeriodCaptureCount(quotaFromContext.rollingCompletedCount);
      return;
    }
    try {
      const n = await countCompletedCapturesInRollingWindow(
        studioUidForUsage,
        ROLLING_CAPTURE_PERIOD_MS,
        user.planStartedAt,
      );
      setProPeriodCaptureCount(n);
    } catch {
      setProPeriodCaptureCount(0);
    }
  }, [
    planKey,
    quotaFromContext?.rollingCompletedCount,
    studioUidForUsage,
    user?.planStartedAt,
    user?.uid,
  ]);

  useEffect(() => {
    if (!user?.uid || !studioUidForUsage) return undefined;
    if (planKey !== 'free') {
      if (metricsFromContext == null) setMetricsCaptureTotal(null);
      if (quotaFromContext?.completedCount == null) setStudioVerifiedCaptureTotal(null);
      return undefined;
    }
    if (metricsFromContext != null) return undefined;
    const ref = doc(db, 'users', studioUidForUsage, 'billing', 'metrics');
    const unsub = onSnapshot(
      ref,
      (snap) => {
        const n = snap.exists?.() ? snap.data()?.captureTotals?.totalCaptures : 0;
        setMetricsCaptureTotal(Number(n) || 0);
      },
      () => {
        setMetricsCaptureTotal(0);
      },
    );
    return () => unsub();
  }, [metricsFromContext, planKey, quotaFromContext?.completedCount, studioUidForUsage, user?.uid]);

  useEffect(() => {
    if (!user?.uid || planKey !== 'free') return undefined;
    refreshStudioCaptureTotal();
    if (quotaFromContext?.completedCount != null) return undefined;
    const intervalMs = 60000;
    const id = setInterval(refreshStudioCaptureTotal, intervalMs);
    const onVis = () => {
      if (document.visibilityState === 'visible') refreshStudioCaptureTotal();
    };
    document.addEventListener('visibilitychange', onVis);
    return () => {
      clearInterval(id);
      document.removeEventListener('visibilitychange', onVis);
    };
  }, [planKey, quotaFromContext?.completedCount, refreshStudioCaptureTotal, studioUidForUsage, user?.uid]);

  useEffect(() => {
    if (!user?.uid || planKey !== 'free' || metricsCaptureTotal == null) return undefined;
    if (quotaFromContext?.completedCount != null) return undefined;
    const t = setTimeout(refreshStudioCaptureTotal, 1000);
    return () => clearTimeout(t);
  }, [
    metricsCaptureTotal,
    planKey,
    quotaFromContext?.completedCount,
    refreshStudioCaptureTotal,
    studioUidForUsage,
    user?.uid,
  ]);

  useEffect(() => {
    if (!user?.uid || !studioUidForUsage) return undefined;
    if (planKey !== 'professional' && planKey !== 'studio') {
      if (quotaFromContext?.rollingCompletedCount == null) setProPeriodCaptureCount(null);
      return undefined;
    }
    refreshProPeriodCaptureCount();
    if (quotaFromContext?.rollingCompletedCount != null) return undefined;
    const intervalMs = 60000;
    const id = setInterval(refreshProPeriodCaptureCount, intervalMs);
    const onVis = () => {
      if (document.visibilityState === 'visible') refreshProPeriodCaptureCount();
    };
    const onPlanRefresh = () => {
      refreshProPeriodCaptureCount();
    };
    document.addEventListener('visibilitychange', onVis);
    window.addEventListener('starling:refresh-user-plan', onPlanRefresh);
    return () => {
      clearInterval(id);
      document.removeEventListener('visibilitychange', onVis);
      window.removeEventListener('starling:refresh-user-plan', onPlanRefresh);
    };
  }, [
    planKey,
    quotaFromContext?.rollingCompletedCount,
    refreshProPeriodCaptureCount,
    studioUidForUsage,
    user?.uid,
  ]);

  const lifetimeCaptureCount = useMemo(
    () => mergeFreeLifetimeCaptureTotals(metricsCaptureTotal, studioVerifiedCaptureTotal),
    [metricsCaptureTotal, studioVerifiedCaptureTotal],
  );

  return { planKey, lifetimeCaptureCount, proPeriodCaptureCount };
}
